// SPDX-FileCopyrightText: © 2023 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { Replace } from "type-fest";

export type Attributes = Record<string, string | undefined>;

/**
 * Elements inside of web components sometimes need to inherit global attributes
 * set on the host. For example, the inner input in `qds-input` should inherit
 * the `tabindex` attribute that developers set directly on `qds-input`. This
 * helper function should be called in componentWillLoad and assigned to a variable
 * that is later used in the render function.
 *
 * This does not need to be reactive as changing attributes on the host element
 * does not trigger a re-render.
 *
 * @param element Element to inherit attributes from.
 * @param attributes Which attributes to inherit.
 */
export const inheritAttributes = (
  element: Element,
  attributes: string[] = [],
): Attributes => {
  const attributeObject: Attributes = {};

  for (const attribute of attributes) {
    const value = element.getAttribute(attribute);
    if (value !== null) {
      attributeObject[attribute] = value;
      element.removeAttribute(attribute);
    }
  }

  return attributeObject;
};

type ARIAAttribute =
  | Replace<Lowercase<keyof ARIAMixin>, "aria", "aria-">
  | "aria-activedescendant"
  | "aria-braillelabel"
  | "aria-brailleroledescription"
  | "aria-colindextext"
  | "aria-controls"
  | "aria-describedby"
  | "aria-description"
  | "aria-details"
  | "aria-errormessage"
  | "aria-flowto"
  | "aria-labelledby"
  | "aria-owns"
  | "aria-relevant"
  | "aria-rowindextext";

const ariaAttributes: ARIAAttribute[] = [
  "aria-activedescendant",
  "aria-atomic",
  "aria-autocomplete",
  "aria-braillelabel",
  "aria-brailleroledescription",
  "aria-busy",
  "aria-checked",
  "aria-colcount",
  "aria-colindex",
  "aria-colindextext",
  "aria-colspan",
  "aria-controls",
  "aria-current",
  "aria-describedby",
  "aria-description",
  "aria-details",
  "aria-disabled",
  "aria-errormessage",
  "aria-expanded",
  "aria-flowto",
  "aria-haspopup",
  "aria-hidden",
  "aria-invalid",
  "aria-keyshortcuts",
  "aria-label",
  "aria-labelledby",
  "aria-level",
  "aria-live",
  "aria-multiline",
  "aria-multiselectable",
  "aria-orientation",
  "aria-owns",
  "aria-placeholder",
  "aria-posinset",
  "aria-pressed",
  "aria-readonly",
  "aria-relevant",
  "aria-required",
  "aria-roledescription",
  "aria-rowcount",
  "aria-rowindex",
  "aria-rowindextext",
  "aria-rowspan",
  "aria-selected",
  "aria-setsize",
  "aria-sort",
  "aria-valuemax",
  "aria-valuemin",
  "aria-valuenow",
  "aria-valuetext",
  "role",
];

/**
 * Returns an array of aria attributes that should be copied from
 * the shadow host element to a target within the light DOM.
 *
 * @param element The element that the attributes should be copied from.
 */
export const inheritAriaAttributes = (
  element: Element,
): ReturnType<typeof inheritAttributes> =>
  inheritAttributes(element, ariaAttributes);

/**
 * From an object pick a set of keys.
 *
 * @param object Object to pick from.
 * @param keys They keys to pick.
 * @returns New object with only the picked keys.
 */
const pick = <T, K extends keyof T>(object: T, ...keys: K[]): Pick<T, K> => {
  const returnValue: Partial<Pick<T, K>> = {};
  for (const key of keys) returnValue[key] = object[key];
  return returnValue as Pick<T, K>;
};

export type QdsFocusEventDetail = Pick<FocusEvent, "relatedTarget">;
export type QdsInputEventDetail = Pick<
  InputEvent,
  "data" | "inputType" | "isComposing"
>;
export const pickFocusEventAttributes = (
  event: FocusEvent,
): QdsFocusEventDetail => pick(event, "relatedTarget");
export const pickInputEventAttributes = (
  event: InputEvent,
): QdsInputEventDetail => pick(event, "data", "inputType", "isComposing");
